"use client";

import React, { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

export default function DivjeBabeSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el) => {
              el.classList.add("revealed");
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="divje-babe"
      ref={sectionRef}
      className="divje-bg relative py-28 md:py-36 overflow-hidden">
      
      {/* Gold top divider */}
      <div className="gold-divider absolute top-0 left-0 right-0" />
      <div className="gold-divider absolute bottom-0 left-0 right-0" />
      {/* Decorative background text */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden"
        aria-hidden="true">
        
        <span
          className="font-display font-black uppercase tracking-widest whitespace-nowrap"
          style={{
            fontSize: "clamp(5rem, 15vw, 12rem)",
            color: "rgba(242,201,76,0.04)",
            letterSpacing: "0.2em"
          }}>
          
          HERITAGE
        </span>
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-12 reveal-hidden">
          <div className="gold-divider flex-1" style={{ maxWidth: "60px" }} />
          <span
            className="badge-yellow px-4 py-1.5 rounded-full text-xs font-display font-bold uppercase tracking-[0.2em]">
            
            ✦ Exclusive Experience
          </span>
          <div className="gold-divider flex-1" style={{ maxWidth: "60px" }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div className="space-y-8">
            <div className="reveal-hidden">
              <h2
                className="font-display font-black leading-tight tracking-tight"
                style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.5rem)" }}>
                
                Divje Babe Flute —
                <br />
                <span className="text-yellow">Interactive Musical</span>
                <br />
                <span style={{ color: "rgba(255,255,255,0.5)" }}>Experience</span>
              </h2>
            </div>

            <div className="reveal-hidden stagger-1">
              <div
                className="w-16 h-1 rounded-full mb-6"
                style={{ background: "linear-gradient(90deg, #F2C94C, transparent)" }} />
              
              <p
                className="text-base md:text-lg leading-relaxed"
                style={{ color: "rgba(255,255,255,0.65)" }}>
                
                Inspired by the world's oldest known musical instrument — the Divje Babe Flute, dating back over 60,000 years — this is our tribute to humanity's deepest musical heritage.
              </p>
              <p
                className="mt-4 text-base leading-relaxed"
                style={{ color: "rgba(255,255,255,0.5)" }}>
                
                At Hi-Hats, we offer an{" "}
                <span className="text-yellow font-semibold">immersive, hands-on exploration</span>{" "}
                of ancient musical forms alongside modern techniques. Students discover how rhythm,
                tone, and melody have shaped human culture across millennia.
              </p>
            </div>

            {/* Highlights */}
            <div className="space-y-4 reveal-hidden stagger-2">
              {[
              { icon: "🏺", text: "Ancient instrument recreation & demonstration" },
              { icon: "🎵", text: "Cross-cultural musical heritage exploration" },
              { icon: "🔬", text: "Acoustic science behind primitive instruments" },
              { icon: "🎭", text: "Exclusive workshop experience for enrolled students" }]?.
              map((item) =>
              <div key={item?.text} className="flex items-start gap-4">
                  <span className="text-xl flex-shrink-0 mt-0.5">{item?.icon}</span>
                  <p
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(255,255,255,0.6)" }}>
                  
                    {item?.text}
                  </p>
                </div>
              )}
            </div>

            <div className="reveal-hidden stagger-3">
              <a
                href="#enroll"
                className="btn-yellow inline-block px-8 py-4 rounded-xl text-sm font-display font-black tracking-wide">
                
                Explore This Experience →
              </a>
            </div>
          </div>

          {/* Right: Editorial Image */}
          <div className="relative reveal-hidden stagger-2 pb-8 md:pb-0">
            {/* Main image */}
            <div
              className="gallery-item rounded-3xl overflow-hidden"
              style={{ height: "480px", position: "relative" }}>
              
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1e9be6cac-1772041641847.png"
                alt="Ancient bone flute representing musical heritage exploration at Hi-Hats academy"
                className="w-full h-full object-cover" />
              
              <div
                className="absolute inset-0"
                style={{
                  background:
                  "linear-gradient(135deg, rgba(242,201,76,0.15) 0%, transparent 50%, rgba(41,171,226,0.1) 100%)"
                }} />
              
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(to top, rgba(17,17,17,0.7) 0%, transparent 50%)"
                }} />
              
            </div>

            {/* Floating info card */}
            <div
              className="absolute -bottom-6 -right-4 md:-right-8 rounded-2xl p-5 max-w-xs"
              style={{
                background: "rgba(17,17,17,0.95)",
                border: "1px solid rgba(242,201,76,0.3)",
                backdropFilter: "blur(16px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
              }}>
              
              <p
                className="text-xs font-display font-bold uppercase tracking-widest mb-2"
                style={{ color: "#F2C94C" }}>
                
                ✦ Did You Know?
              </p>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
                The Divje Babe Flute is estimated to be{" "}
                <span className="text-yellow font-bold">60,000+ years old</span> — making it the
                oldest known musical instrument ever discovered.
              </p>
            </div>

            {/* Year badge */}
            <div
              className="absolute top-6 left-6 rounded-xl px-4 py-2"
              style={{
                background: "rgba(17,17,17,0.85)",
                border: "1px solid rgba(255,255,255,0.1)",
                backdropFilter: "blur(12px)"
              }}>
              
              <p
                className="text-xs font-display font-bold uppercase tracking-widest"
                style={{ color: "rgba(255,255,255,0.5)" }}>
                
                Heritage
              </p>
              <p className="font-display font-black text-xl text-yellow">60,000 BC</p>
            </div>
          </div>
        </div>
      </div>
    </section>);

}